import React from 'react';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import Typography from 'src/app/atoms/Typography/Typography';
import { ColorModePropKeys } from 'src/app/styles/colors';
import { cssVar } from 'src/app/styles/helper';
import { SpacingProps } from 'src/app/atoms/Layout/Spacing.styles';
import { BadgeModernStack } from './Badge.styles';

export type BadgeType = 'pill-color' | 'badge-color' | 'badge-modern';

export interface BadgeProps extends SpacingProps {
  label: string;
  color?: 'gray' | 'brand' | 'error' | 'warning' | 'success';
  icon?: React.ReactNode;
  noWrap?: boolean;
  type?: BadgeType;
}

type BadgeColor = {
  text: ColorModePropKeys;
  background: ColorModePropKeys;
  border: ColorModePropKeys;
};

const badgeColors: Record<string, BadgeColor> = {
  gray: {
    text: 'text-secondary',
    background: 'gray-light-50',
    border: 'gray-light-200',
  },
  brand: {
    text: 'utility-brand-700',
    background: 'utility-brand-50',
    border: 'brand-200',
  },
  error: {
    text: 'error-700',
    background: 'error-50',
    border: 'error-200',
  },
  warning: {
    text: 'warning-700',
    background: 'warning-50',
    border: 'warning-200',
  },
  success: {
    text: 'success-700',
    background: 'success-50',
    border: 'success-200',
  },
};

const Badge = ({ label, color = 'gray', icon, noWrap = false, type = 'pill-color', ...spacingProps }: BadgeProps): JSX.Element => {
  const isModern = type === 'badge-modern';
  const StackComponent = isModern ? BadgeModernStack : Stack;
  const borderRadius = type === 'pill-color' ? 'radius-full' : 'radius-sm';
  const background = isModern ? 'bg-primary' : badgeColors[color].background;
  const border = isModern
    ? `1px solid ${cssVar('border-primary')}`
    : `1px solid ${cssVar(badgeColors[color].border)}`;
  const textColor = isModern ? 'text-secondary' : badgeColors[color].text;

  return (
    <StackComponent
      direction='row'
      align='center'
      gap='spacing-sm'
      borderRadius={borderRadius}
      background={background}
      border={border}
      padding={['spacing-xxs', 'spacing-md']}
      {...spacingProps}
    >
      {icon}
      <Typography
        type='paragraph'
        size='sm'
        color={textColor}
        fontWeight='font-weight-medium'
        whiteSpace={noWrap ? 'nowrap' : undefined}
      >
        {label}
      </Typography>
    </StackComponent>
  );
};

export default Badge;
